import { zodResolver } from '@hookform/resolvers/zod'
import { useLocation } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import StarFilledIcon from '@/assets/icons/star-filled.svg?react'
import StarIcon from '@/assets/icons/star.svg?react'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import FormField from '@/components/FormField'
import Input from '@/components/Input/Input'
import ModalForm from '@/components/Modal/ModalForm'
import useAddFavourite from '@/mutations/useAddFavourite'
import useRemoveFavourite from '@/mutations/useRemoveFavourite'
import useSavedLinks from '@/queries/useFavourites'
import {
  checkIfLocationMatchesString,
  locationToString
} from '@/utils/location'
import { requiredString } from '@/utils/zod'

const ToggleFavourite = () => {
  const location = useLocation()
  const { data: savedLinks, refetch: refetchSavedLinks } = useSavedLinks()

  const id = (savedLinks ?? []).find(({ url }) =>
    checkIfLocationMatchesString(location, url)
  )?.id

  if (id) return <RemoveFavourite id={id} onSuccess={refetchSavedLinks} />
  return <AddFavourite onSuccess={refetchSavedLinks} />
}

type RemoveFavouriteProps = { id: string; onSuccess: () => void }

const RemoveFavourite = (props: RemoveFavouriteProps) => {
  const { t } = useTranslation('common')

  const { mutate: removeFavourite } = useRemoveFavourite({
    onSuccess: props.onSuccess
  })

  return (
    <ButtonIcon
      size="medium"
      variant="secondary"
      ariaLabel={t('button.remove-favourite')}
      onClick={() => removeFavourite({ id: props.id })}
    >
      <StarFilledIcon />
    </ButtonIcon>
  )
}

type AddFavouriteProps = {
  onSuccess: () => void
}

const addFavouriteFormSchema = z.object({ name: requiredString() })

const AddFavourite = (props: AddFavouriteProps) => {
  const { t } = useTranslation('common')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const location = useLocation()

  const { mutate: addFavourite } = useAddFavourite({
    onSuccess: () => {
      setIsModalOpen(false)
      props.onSuccess()
    }
  })

  const form = useForm({
    defaultValues: {
      name: ''
    },
    resolver: zodResolver(addFavouriteFormSchema)
  })

  return (
    <ModalForm
      form={form}
      onSubmit={({ name }) =>
        addFavourite({ name, url: locationToString(location) })
      }
      title={t('header.add-to-favourites')}
      open={isModalOpen}
      submitText={t('button.save')}
      trigger={
        <ButtonIcon
          size="medium"
          variant="secondary"
          ariaLabel={t('button.add-favourite')}
          onClick={() => setIsModalOpen(true)}
        >
          <StarIcon />
        </ButtonIcon>
      }
      onClose={() => setIsModalOpen(false)}
      onOpenChange={setIsModalOpen}
    >
      <FormField
        id="favourite-name"
        label={t('label.name')}
        name="name"
        control={form.control}
        required
        render={({ inputProps }) => (
          <Input {...inputProps} placeholder={t('placeholder.type-name')} />
        )}
      />
    </ModalForm>
  )
}

export default ToggleFavourite
